import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    FormControl,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    HttpTransportType,
    LogLevel,
} from '@microsoft/signalr';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import { useApplicationContext } from 'ApplicationContext';
import {
    GeodataConversionAvailabilityState,
    GeodataConversionJobInfo,
    GeodataConversionJobStatus,
    GeodataConversionOutputFormat,
} from 'apiClient/Api';
import FileUpload from '../../Components/Admin/CoreDumps/FileUpload';
import { SupportIcon } from '../../SupportIcon';

type OutputFormat = 'pmtiles' | 'mbtiles';

const HUB_PATH = '/hubs/geodata-conversion';
const REGISTER_ALL = 'RegisterAllJobs';
const UNREGISTER_ALL = 'UnregisterAllJobs';
const EVENT_CREATED = 'geodatajobcreated';
const EVENT_STATUS = 'geodatajobstatuschanged';
const EVENT_PROGRESS = 'geodatajobprogress';
const EVENT_DELETED = 'geodatajobdeleted';

interface ProgressEvent {
    jobId: string;
    progressPercent: number;
    statusMessage?: string | null;
}

interface DeletedEvent {
    jobId: string;
}

const isTerminal = (status?: GeodataConversionJobStatus) =>
    status === GeodataConversionJobStatus.Completed ||
    status === GeodataConversionJobStatus.Failed ||
    status === GeodataConversionJobStatus.Cancelled ||
    status === GeodataConversionJobStatus.Expired;

const statusChipColor = (
    status?: GeodataConversionJobStatus,
): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
    switch (status) {
        case GeodataConversionJobStatus.Queued:
            return 'default';
        case GeodataConversionJobStatus.Running:
            return 'primary';
        case GeodataConversionJobStatus.Completed:
            return 'success';
        case GeodataConversionJobStatus.Failed:
            return 'error';
        case GeodataConversionJobStatus.Cancelled:
        case GeodataConversionJobStatus.Expired:
            return 'warning';
        default:
            return 'default';
    }
};

const formatLabel = (format?: GeodataConversionOutputFormat) =>
    format === GeodataConversionOutputFormat.Mbtiles ? 'MBTiles' : 'PMTiles';

const GeodataConverter: React.FC = () => {
    const { t } = useTranslation();
    const context = useApplicationContext();
    const { api, apiError, baseUrl, token } = context;

    const [availability, setAvailability] = useState<GeodataConversionAvailabilityState | null>(null);
    const [availabilityChecked, setAvailabilityChecked] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [outputFormat, setOutputFormat] = useState<OutputFormat>('pmtiles');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number | undefined>(undefined);
    const [jobs, setJobs] = useState<GeodataConversionJobInfo[]>([]);
    const [downloadingJobId, setDownloadingJobId] = useState<string | null>(null);
    const [realtimeConnected, setRealtimeConnected] = useState(false);

    const isPbf =
        !!file &&
        (file.name.toLowerCase().endsWith('.osm.pbf') || file.name.toLowerCase().endsWith('.pbf'));

    const upsertJob = useCallback((job: GeodataConversionJobInfo) => {
        if (!job?.id) return;
        setJobs((prev) => {
            const idx = prev.findIndex((j) => j.id === job.id);
            if (idx === -1) return [job, ...prev];
            const next = prev.slice();
            next[idx] = { ...next[idx], ...job };
            return next;
        });
    }, []);

    const removeJob = useCallback((jobId: string) => {
        setJobs((prev) => prev.filter((j) => j.id !== jobId));
    }, []);

    const reloadJobs = useCallback(async () => {
        try {
            const response = await api.geodata.geodataListJobs();
            setJobs(response.data ?? []);
        } catch (error) {
            apiError(error);
        }
    }, [api, apiError]);

    useEffect(() => {
        let cancelled = false;
        api.geodata
            .geodataHealth()
            .then((res) => {
                if (cancelled) return;
                setAvailability(res.data);
            })
            .catch((error) => {
                if (cancelled) return;
                const data = error?.response?.data;
                if (data && typeof data === 'object' && 'isAvailable' in data) {
                    setAvailability(data as GeodataConversionAvailabilityState);
                } else {
                    setAvailability({ isAvailable: false, reason: error?.message });
                }
            })
            .finally(() => {
                if (!cancelled) setAvailabilityChecked(true);
            });
        return () => {
            cancelled = true;
        };
    }, [api]);

    useEffect(() => {
        reloadJobs();
    }, [reloadJobs]);

    const connectionRef = useRef<HubConnection | null>(null);

    useEffect(() => {
        if (!token) {
            setRealtimeConnected(false);
            return;
        }

        const connection = new HubConnectionBuilder()
            .withUrl(`${baseUrl}${HUB_PATH}`, {
                accessTokenFactory: () => token ?? '',
                transport: HttpTransportType.WebSockets,
                skipNegotiation: true,
            })
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Information)
            .build();

        connection.on(EVENT_CREATED, (job: GeodataConversionJobInfo) => upsertJob(job));
        connection.on(EVENT_STATUS, (job: GeodataConversionJobInfo) => upsertJob(job));
        connection.on(EVENT_PROGRESS, (payload: ProgressEvent) => {
            if (!payload?.jobId) return;
            setJobs((prev) =>
                prev.map((j) =>
                    j.id === payload.jobId
                        ? {
                              ...j,
                              progressPercent: payload.progressPercent,
                              statusMessage: payload.statusMessage ?? j.statusMessage,
                          }
                        : j,
                ),
            );
        });
        connection.on(EVENT_DELETED, (payload: DeletedEvent) => {
            if (payload?.jobId) removeJob(payload.jobId);
        });

        connection.onclose(() => setRealtimeConnected(false));
        connection.onreconnected(async () => {
            try {
                await connection.invoke(REGISTER_ALL);
                setRealtimeConnected(true);
                reloadJobs();
            } catch {
                setRealtimeConnected(false);
            }
        });
        connection.onreconnecting(() => setRealtimeConnected(false));

        let aborted = false;
        connection
            .start()
            .then(async () => {
                if (aborted) {
                    await connection.stop();
                    return;
                }
                await connection.invoke(REGISTER_ALL);
                setRealtimeConnected(true);
            })
            .catch((err) => {
                if (aborted) return;
                console.error('[GeodataConverter] SignalR connection error:', err);
                setRealtimeConnected(false);
            });

        connectionRef.current = connection;

        return () => {
            aborted = true;
            (async () => {
                try {
                    if (connection.state === HubConnectionState.Connected) {
                        await connection.invoke(UNREGISTER_ALL);
                    }
                } finally {
                    await connection.stop();
                    setRealtimeConnected(false);
                    connectionRef.current = null;
                }
            })();
        };
    }, [baseUrl, token, upsertJob, removeJob, reloadJobs]);

    const handleSubmit = async () => {
        if (!file || !isPbf) return;

        setIsSubmitting(true);
        setUploadProgress(0);

        try {
            const response = await api.geodata.geodataSubmitJob(
                {
                    PbfFile: file,
                    OutputFormat: outputFormat,
                },
                {
                    onUploadProgress: (progressEvent: any) => {
                        if (progressEvent.total) {
                            setUploadProgress(
                                Math.round((progressEvent.loaded * 100) / progressEvent.total),
                            );
                        }
                    },
                },
            );
            if (response.data) {
                upsertJob(response.data);
            }
            setFile(null);
        } catch (error) {
            apiError(error);
        } finally {
            setIsSubmitting(false);
            setUploadProgress(undefined);
        }
    };

    const handleDownload = async (job: GeodataConversionJobInfo) => {
        if (!job.id) return;
        setDownloadingJobId(job.id);
        try {
            const response = await axios.get(`${baseUrl}/Geodata/jobs/${job.id}/result`, {
                responseType: 'blob',
                headers: { Authorization: `Bearer ${token}` },
            });
            const url = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download =
                job.outputFileName ?? `${job.id}.${formatLabel(job.outputFormat).toLowerCase()}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            apiError(error);
        } finally {
            setDownloadingJobId(null);
        }
    };

    const handleDelete = async (job: GeodataConversionJobInfo) => {
        if (!job.id) return;
        try {
            await api.geodata.geodataDeleteJob(job.id);
            removeJob(job.id);
        } catch (error) {
            apiError(error);
        }
    };

    const sortedJobs = useMemo(() => {
        return jobs.slice().sort((a, b) => {
            const ad = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bd = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return bd - ad;
        });
    }, [jobs]);

    const statusLabel = (status?: GeodataConversionJobStatus) => {
        switch (status) {
            case GeodataConversionJobStatus.Queued:
                return t('geodataConverter.status.queued');
            case GeodataConversionJobStatus.Running:
                return t('geodataConverter.status.running');
            case GeodataConversionJobStatus.Completed:
                return t('geodataConverter.status.completed');
            case GeodataConversionJobStatus.Failed:
                return t('geodataConverter.status.failed');
            case GeodataConversionJobStatus.Cancelled:
                return t('geodataConverter.status.cancelled');
            case GeodataConversionJobStatus.Expired:
                return t('geodataConverter.status.expired');
            default:
                return '—';
        }
    };

    const submitDisabled =
        !file ||
        !isPbf ||
        isSubmitting ||
        availability?.isAvailable === false;

    return (
        <Box sx={{ width: 1 }}>
            <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    sx={{
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', md: 'center' },
                    }}
                >
                    <Box>
                        <Typography variant="h6">{t('geodataConverter.title')}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('geodataConverter.description')}
                        </Typography>
                    </Box>
                    <Typography
                        variant="caption"
                        color={realtimeConnected ? 'success.main' : 'text.secondary'}
                    >
                        {realtimeConnected
                            ? t('geodataConverter.live.connected')
                            : t('geodataConverter.live.disconnected')}
                    </Typography>
                </Stack>

                {availabilityChecked && availability && (
                    <Alert
                        severity={availability.isAvailable ? 'success' : 'warning'}
                        sx={{ marginTop: 2 }}
                    >
                        <Typography variant="body2">
                            {availability.isAvailable
                                ? t('geodataConverter.health.available', {
                                      mode: availability.executionMode ?? '—',
                                  })
                                : t('geodataConverter.health.unavailable', {
                                      reason: availability.reason ?? '—',
                                  })}
                        </Typography>
                        {availability.detail && (
                            <Typography variant="caption" component="pre" sx={{ marginTop: 1 }}>
                                {availability.detail}
                            </Typography>
                        )}
                    </Alert>
                )}
            </Paper>

            <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                    {t('geodataConverter.submit.title')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FileUpload
                        onFileChange={setFile}
                        title=""
                        primaryText={t('geodataConverter.dropzone.primary', {
                            defaultValue: 'PBF-Datei hierher ziehen oder zum Auswählen klicken',
                        })}
                        secondaryText={t('geodataConverter.dropzone.secondary', {
                            defaultValue: 'Akzeptiert .osm.pbf · max. einzelne Datei',
                        })}
                        accept=".pbf,.osm.pbf,application/octet-stream"
                    />

                    {file && !isPbf && (
                        <Typography color="error" variant="body2">
                            {t('geodataConverter.invalidFile')}
                        </Typography>
                    )}

                    <FormControl fullWidth>
                        <InputLabel id="geodata-output-format-label">
                            {t('geodataConverter.outputFormat')}
                        </InputLabel>
                        <Select
                            labelId="geodata-output-format-label"
                            value={outputFormat}
                            label={t('geodataConverter.outputFormat')}
                            onChange={(e) => setOutputFormat(e.target.value as OutputFormat)}
                        >
                            <MenuItem value="pmtiles">PMTiles (.pmtiles)</MenuItem>
                            <MenuItem value="mbtiles">MBTiles (.mbtiles)</MenuItem>
                        </Select>
                    </FormControl>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={submitDisabled}
                    >
                        {t('geodataConverter.submit.button')}
                        {isSubmitting && <SupportIcon percentage={uploadProgress} size={24} />}
                    </Button>

                    {isSubmitting && uploadProgress !== undefined && (
                        <Box>
                            <Typography variant="caption">
                                {t('geodataConverter.uploading')}
                            </Typography>
                            <LinearProgress variant="determinate" value={uploadProgress} />
                        </Box>
                    )}
                </Box>
            </Paper>

            <Paper elevation={3} sx={{ padding: 2 }}>
                <Stack
                    direction="row"
                    sx={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 1,
                    }}
                >
                    <Typography variant="subtitle1">{t('geodataConverter.jobs.title')}</Typography>
                    <Button size="small" onClick={reloadJobs}>
                        {t('geodataConverter.jobs.refresh')}
                    </Button>
                </Stack>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('geodataConverter.jobs.fileName')}</TableCell>
                                <TableCell>{t('geodataConverter.jobs.format')}</TableCell>
                                <TableCell>{t('geodataConverter.jobs.status')}</TableCell>
                                <TableCell sx={{ minWidth: 160 }}>
                                    {t('geodataConverter.jobs.progress')}
                                </TableCell>
                                <TableCell>{t('geodataConverter.jobs.createdBy')}</TableCell>
                                <TableCell>{t('geodataConverter.jobs.createdAt')}</TableCell>
                                <TableCell align="right">
                                    {t('geodataConverter.jobs.actions')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedJobs.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <Typography variant="body2" color="text.secondary">
                                            {t('geodataConverter.jobs.empty')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {sortedJobs.map((job) => {
                                const progress = job.progressPercent ?? 0;
                                const isCompleted =
                                    job.status === GeodataConversionJobStatus.Completed;
                                return (
                                    <TableRow key={job.id} hover>
                                        <TableCell>
                                            <Tooltip title={job.errorMessage ?? job.statusMessage ?? ''}>
                                                <span>{job.inputFileName ?? job.id}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>{formatLabel(job.outputFormat)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                color={statusChipColor(job.status)}
                                                label={statusLabel(job.status)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ width: '100%' }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={Math.min(100, Math.max(0, progress))}
                                                    />
                                                </Box>
                                                <Typography variant="caption" sx={{ minWidth: 36 }}>
                                                    {progress}%
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{job.createdByUserName ?? '—'}</TableCell>
                                        <TableCell>
                                            {job.createdAt
                                                ? new Date(job.createdAt).toLocaleString()
                                                : '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack
                                                direction="row"
                                                spacing={0.5}
                                                sx={{ justifyContent: 'flex-end' }}
                                            >
                                                {isCompleted && (
                                                    <Tooltip title={t('geodataConverter.jobs.download')}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDownload(job)}
                                                                disabled={downloadingJobId === job.id}
                                                            >
                                                                <DownloadIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                                <Tooltip
                                                    title={
                                                        isTerminal(job.status)
                                                            ? t('geodataConverter.jobs.delete')
                                                            : t('geodataConverter.jobs.cancel')
                                                    }
                                                >
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDelete(job)}
                                                        >
                                                            {isTerminal(job.status) ? (
                                                                <DeleteIcon fontSize="small" />
                                                            ) : (
                                                                <StopCircleIcon fontSize="small" />
                                                            )}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
};

export default GeodataConverter;
